/**
 * The viewer's assistant conversation, remembered on this device so the panel can connect its socket at once.
 *
 * Opening the panel used to be strictly serial: resolve a transport, POST `conversations`, find the assistant room in
 * the answer, THEN dial the room's socket and wait for its history -- the conversations read alone was ~0.4-0.8s on
 * dev, spent re-learning a room id that never changes. The room is now read from here first and the socket dials
 * immediately; the conversations read still runs, refreshes the rooms list and corrects the cache if the server
 * disagrees (a deleted room, a different assistant conversation), so this is a head start, never a source of truth.
 *
 * KEYED TO THE VIEWER. A device is shared; a room cached for one signed-in user must not open for the next.
 *
 * SHARED, not the chats SPA's own: the app shell reads it too (`hasRememberedAssistantRoom`) to decide whether a
 * mobile pre-warm has anything to re-adopt, and it must ask the same store the pane writes.
 *
 * SPLIT under `max-lines`: the record shape is `types/assistant-room-cache.ts` and the vouch predicate plus the ticket
 * margin are `const/assistant-room-record.ts`; this leaf is the storage I/O only.
 */

import { ASSISTANT_ROOM_CACHE_STORAGE_KEY, DEFAULT_CONVERSATION_TYPE } from '@federa/const/assistant';
import { ROOM_TICKET_CLIENT_MARGIN_SEC, isCachedAssistantRoom } from '@federa/const/assistant-room-record';
import { CHAT_ROOM_TICKET_TTL_SEC } from '@federa/const/websocket';
import { TIME_UNITS } from '@federa/const/time-units';
import { isRecordValue } from '@federa/const/record-guards';
import { documentLocalStorage } from '@federa/const/shared-window-state';
import type { CachedAssistantRoom } from '@federa/types/assistant-room-cache';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';

/** The stored record, parsed; null when nothing is stored. Throws on a throwing store or malformed JSON -- the
 *  readers below treat both as "nothing cached", DELIBERATELY and silently: the conversations read is the path that
 *  always works, and a user's device carries no channel to report on. */
function readStoredRecord(): unknown {
	const raw = documentLocalStorage()?.getItem(ASSISTANT_ROOM_CACHE_STORAGE_KEY);
	return raw ? (JSON.parse(raw) as unknown) : null;
}

/** The record vouched for THIS viewer, else null. */
function readVouchedRecord(userId: string): CachedAssistantRoom | null {
	try {
		const parsed = readStoredRecord();
		return isCachedAssistantRoom(parsed, userId) ? parsed : null;
	} catch {
		return null;
	}
}

export function readCachedAssistantRoom(userId: string): ChatRoomStruct | null {
	return readVouchedRecord(userId)?.room ?? null;
}

/**
 * Whether SOME viewer's assistant room is remembered on this device: the host shell's pre-warm gate
 * (`useAssistantRouteWarm`), which decides whether to build the assistant root before any tap. The host does not
 * know the chats identity the way the pane does, so it asks the device-level question; the pane's own gate
 * (`warmMayOpen`) still refuses to connect a room that is not the current viewer's. A device with no room -- a
 * first-time visitor, guest or member -- gets no pre-warm and nothing is created for them.
 */
export function hasRememberedAssistantRoom(): boolean {
	try {
		const parsed = readStoredRecord();
		return isRecordValue(parsed) && typeof parsed.userId === 'string' && isCachedAssistantRoom(parsed, parsed.userId);
	} catch {
		return false;
	}
}

export function writeCachedAssistantRoom(userId: string, room: ChatRoomStruct, ticket?: string): void {
	if (room.conversationType !== DEFAULT_CONVERSATION_TYPE) return;
	try {
		const record: CachedAssistantRoom = ticket
			? {
					userId,
					room,
					ticket,
					ticketExpiresAt: Date.now() + (CHAT_ROOM_TICKET_TTL_SEC - ROOM_TICKET_CLIENT_MARGIN_SEC) * TIME_UNITS.SECOND_MS,
				}
			: { userId, room };
		documentLocalStorage()?.setItem(ASSISTANT_ROOM_CACHE_STORAGE_KEY, JSON.stringify(record));
	} catch {
		// Silent: DELIBERATELY swallowed and non-fatal. Quota or privacy mode is the whole population here, and the
		// panel simply keeps paying the conversations read first.
	}
}

/** The unexpired room ticket cached for this viewer AND this room, else null (the caller presents its Firebase token).
 *  The two ids ride KEYED, so a caller cannot hand the room over as the viewer (QUAL-MULTI-ID-BARE-STRING-001). */
export function readCachedAssistantTicket({ userId, roomId }: { readonly userId: string; readonly roomId: string }): string | null {
	const record = readVouchedRecord(userId);
	if (!record || record.room.id !== roomId) return null;
	const { ticket, ticketExpiresAt } = record;
	return typeof ticket === 'string' && ticket && typeof ticketExpiresAt === 'number' && ticketExpiresAt > Date.now() ? ticket : null;
}

export function clearCachedAssistantRoom(): void {
	try {
		documentLocalStorage()?.removeItem(ASSISTANT_ROOM_CACHE_STORAGE_KEY);
	} catch {
		// Silent: DELIBERATELY swallowed and non-fatal. Nothing to recover -- a store that cannot be cleared cannot be
		// read either, so the stale entry is unreachable by the same fault that left it.
	}
}
